import {
    Permissions,
    Roles,
    hasAnyPermission,
    hasAnyRole,
    hasHealthAnalysisAccess,
    hasInsuranceNewsAccess,
} from '../../auth/rbac'
import { isOrgPaysCurrentlyEffective } from '../billingPaywallNudge'


/**
 * Shell/App.js 기준 메뉴 노출 컨텍스트.
 * Phase 2에서 BE visibleMenuKeys와 병합할 때 동일 키 이름을 유지한다.
 */
export function hasOrgPaysEntitlementSignal(user) {
    if (!user || typeof user !== 'object') return false
    if (isOrgPaysCurrentlyEffective(user.orgContract || user.org_contract)) return true
    if (user.orgContractPrimary === true || user.org_contract_primary === true) return true
    return Boolean(
        user.orgPaysEntitlement === true ||
        user.org_pays_entitlement === true ||
        user.hasOrgPaysEntitlement === true ||
        user.has_org_pays_entitlement === true ||
        user.orgContractCurrentlyEffective === true ||
        user.org_contract_currently_effective === true,
    )
}
export function buildMenuVisibilityContext(user, authHelpers = {}) {
    const hasRole = authHelpers.hasAnyRole || ((roles) => hasAnyRole(user, roles))
    const hasPermission = authHelpers.hasAnyPermission || ((perms) => hasAnyPermission(user, perms))

    const isOperator = hasRole([Roles.OPERATOR])
    const hasUser = Boolean(user)
    const isPersonalOrgMember = String(user?.orgType || '').toUpperCase() === 'PERSONAL'
    const hasOrgContractEntitlement = isOperator || hasOrgPaysEntitlementSignal(user)

    const showOrgs =
        isOperator ||
        (hasPermission([Permissions.ORG_MANAGE]) && !isPersonalOrgMember)
    const showOrgBanners =
        isOperator ||
        (hasPermission([Permissions.ORG_BANNER_MANAGE, Permissions.ORG_MANAGE]) &&
            !isPersonalOrgMember &&
            hasOrgContractEntitlement)
    const showOrgHomepageSales = isOperator || (!isPersonalOrgMember && hasOrgContractEntitlement)
    const showOrgHomepage = showOrgHomepageSales
    const showUsers =
        isOperator ||
        (hasPermission([Permissions.USER_INVITE, Permissions.ORG_MANAGE]) && !isPersonalOrgMember)
    const showRequests = hasUser
    const showNotices = hasUser && !isPersonalOrgMember
    const showPersonalCounterparts = hasUser && isPersonalOrgMember
    const showReports = isOperator || hasPermission([Permissions.REPORT_VIEW])
    const hasOrgManagePermission = hasPermission([Permissions.ORG_MANAGE])
    const showOrgDashboard =
        isOperator ||
        (!isPersonalOrgMember &&
            hasOrgContractEntitlement &&
            hasOrgManagePermission)
    const showHealthAnalysis = Boolean(
        user && (isOperator || hasHealthAnalysisAccess(user)),
    )
    const showFeatureEntryMenus = Boolean(user)
    const enableTermsLibrary =
        String(process.env.REACT_APP_ENABLE_TERMS_LIBRARY || '').toLowerCase() === 'true'
    const showTermsLibrary = enableTermsLibrary && isOperator
    const showFireBuildingRegistry = showHealthAnalysis
    const showUw3nnExceptionsLookup = showHealthAnalysis
    const showOperatorDesignTools = isOperator
    const showSupportInquiries = isOperator
    const showClaims = Boolean(user)
    const showInsuranceNews = Boolean(user && hasInsuranceNewsAccess(user))
    const showTemplateManage =
        isOperator || hasPermission([Permissions.TEMPLATE_MANAGE])

    return {
        isOperator,
        isGA: false,
        isInsurer: false,
        isPersonalOrgMember,
        showDashboard: true,
        showOrgs,
        showOrgBanners,
        showOrgHomepageSales,
        showOrgHomepage,
        showUsers,
        showRequests,
        showNotices,
        showPersonalTaskBoard: Boolean(user),
        showPersonalCounterparts,
        showSalesImages: true,
        showKakaoBulkSender: true,
        showBulkSms: false,
        showWorkSupportTools: showHealthAnalysis,
        showReports,
        showOrgDashboard,
        showHealthAnalysis,
        showFeatureEntryMenus,
        showTermsLibrary,
        showFireBuildingRegistry,
        showUw3nnExceptionsLookup,
        showOperatorDesignTools,
        showSupportInquiries,
        showClaims,
        showSurveyManagement: true,
        showCustomerManagement: true,
        showCustomerSupportTools: true,
        showCustomerRegistration: isOperator,
        showInsuranceNews,
        showDataMgmtMenu: isOperator,
        showOpsMgmtMenu: isOperator,
        showSalesSupportMenu: true,
        showDesignSupportMenu:
            showFeatureEntryMenus ||
            showHealthAnalysis ||
            showFireBuildingRegistry ||
            showUw3nnExceptionsLookup ||
            showRequests,
        showAdminMenu: !isPersonalOrgMember && (showNotices || showOrgs || showUsers || showOrgDashboard || showOrgBanners),
        showTemplateManage,
        always: true,
        never: false,
    }
}

// showCustomerSupportMenu depends on other flags — compute after object creation
export function finalizeMenuVisibilityContext(ctx) {
    return {
        ...ctx,
        showCustomerSupportMenu:
            ctx.showCustomerManagement ||
            ctx.showCustomerSupportTools ||
            ctx.showSurveyManagement ||
            ctx.showSalesImages ||
            ctx.showKakaoBulkSender ||
            ctx.showClaims,
        showWorkSupportMenu:
            ctx.showPersonalTaskBoard ||
            ctx.showPersonalCounterparts ||
            ctx.isOperator ||
            ctx.showInsuranceNews ||
            ctx.showWorkSupportTools,
    }
}

export function createMenuVisibilityContext(user, authHelpers = {}) {
    const base = finalizeMenuVisibilityContext(buildMenuVisibilityContext(user, authHelpers))
    const raw = user?.visibleMenuKeys ?? user?.visible_menu_keys
    const visibleMenuKeys = Array.isArray(raw) ? raw.filter(Boolean) : null
    const showOrgHomepageSales = visibleMenuKeys
        ? (base.isOperator || (!base.isPersonalOrgMember && visibleMenuKeys.includes('MENU_SALES_ORG_HOMEPAGE')))
        : base.showOrgHomepageSales
    const showOrgBanners = visibleMenuKeys
        ? (base.isOperator || (!base.isPersonalOrgMember && visibleMenuKeys.includes('MENU_ORG_BANNERS')))
        : base.showOrgBanners
    const showOrgDashboard = visibleMenuKeys
        ? (base.isOperator || (!base.isPersonalOrgMember && visibleMenuKeys.includes('MENU_ORG_DASHBOARD')))
        : base.showOrgDashboard
    return {
        ...base,
        showOrgBanners,
        showOrgHomepageSales,
        showOrgHomepage: showOrgHomepageSales,
        showOrgDashboard,
        showAdminMenu: !base.isPersonalOrgMember && (base.showAdminMenu || showOrgBanners || showOrgDashboard),
        visibleMenuKeys,
    }
}

/**
 * @param {object} item menu manifest item
 * @param {object} ctx createMenuVisibilityContext 결과
 * @param {{ audience?: string }} [options]
 */
export function isMenuItemVisible(item, ctx, options = {}) {
    if (!item) return false
    const audience = options.audience || 'default'
    const key =
        (item.visibilityByAudience && item.visibilityByAudience[audience]) ||
        item.visibilityKey ||
        'never'
    if (key === 'never') return false
    if (key !== 'always' && !Boolean(ctx[key])) return false

    if (ctx.visibleMenuKeys && item.menuKey) {
        if (ctx.isOperator) return true
        return ctx.visibleMenuKeys.includes(item.menuKey)
    }

    return key === 'always' || Boolean(ctx[key])
}

export function filterVisibleMenuItems(items, ctx, options = {}) {
    const { surface, audience } = options
    return (items || []).filter((item) => {
        if (surface && item.surfaces && !item.surfaces.includes(surface)) {
            return false
        }
        return isMenuItemVisible(item, ctx, { audience })
    })
}

/** Phase 2 BE visibleMenuKeys 연동용 */
export function getVisibleMenuKeys(items, ctx, options = {}) {
    return filterVisibleMenuItems(items, ctx, options)
        .map((item) => item.menuKey)
        .filter(Boolean)
}
